<template>
  <v-app>
    <!-- ─── App Bar ─────────────────────────────────────────────────── -->
    <v-app-bar
      :elevation="scrolled ? 2 : 0"
      :class="['app-bar', { 'app-bar--scrolled': scrolled }]"
      density="comfortable"
    >
      <!-- Hamburger: desktop always, mobile only on non-primary routes -->
      <v-app-bar-nav-icon
        v-if="!isMobile || !isPrimaryRoute"
        @click="toggleDrawer"
        class="ml-1"
      />

      <!-- Back button on mobile detail pages -->
      <v-btn
        v-if="isMobile && !isPrimaryRoute"
        icon="mdi-arrow-left"
        variant="text"
        @click="$router.back()"
        class="ml-1"
      />

      <v-toolbar-title class="app-bar__title">
        <span class="title-text">{{ appStore.title }}</span>
      </v-toolbar-title>

      <v-spacer />

      <!-- Theme toggle -->
      <v-btn
        :icon="appStore.themeIcon"
        variant="text"
        size="small"
        @click="handleCycleTheme"
      />

      <!-- User menu -->
      <v-menu v-if="authStore.isAuthenticated" :close-on-content-click="true">
        <template #activator="{ props }">
          <v-btn
            icon="mdi-account-circle"
            variant="text"
            size="small"
            v-bind="props"
            class="mr-1"
          />
        </template>
        <v-list density="compact" min-width="200">
          <v-list-item v-if="authStore.currentUser" class="pb-1">
            <v-list-item-title class="font-weight-medium">
              {{ authStore.currentUser.email || authStore.currentUser.username || 'User' }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="authStore.hasValidToken" class="text-success text-caption">
              API Token Active
            </v-list-item-subtitle>
          </v-list-item>
          <v-divider class="mb-1" />
          <v-list-item prepend-icon="mdi-cog" to="/settings" title="Settings" />
          <v-list-item prepend-icon="mdi-logout" title="Logout" @click="handleLogout" />
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- ─── Side Navigation Drawer ─────────────────────────────────── -->
    <v-navigation-drawer v-model="drawer" temporary class="nav-drawer">
      <div class="nav-drawer__header pa-4">
        <div class="d-flex align-center gap-3">
          <v-icon icon="mdi-rss" color="primary" size="28" />
          <span class="text-h6 font-weight-semibold">Rivulet</span>
        </div>
      </div>
      <v-divider />
      <v-list nav density="compact" class="pa-2">
        <v-list-item
          v-for="item in navigationItems"
          :key="item.title"
          :to="item.to"
          :prepend-icon="item.icon"
          :title="item.title"
          rounded="lg"
          class="mb-1"
        />
      </v-list>
    </v-navigation-drawer>

    <!-- ─── Main Content ────────────────────────────────────────────── -->
    <v-main :class="{ 'has-bottom-nav': isMobile }">
      <v-container fluid class="mobile-content-pad">
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </v-container>
    </v-main>

    <!-- ─── Bottom Navigation (mobile only) ────────────────────────── -->
    <v-bottom-navigation
      v-if="isMobile"
      :model-value="bottomNavValue"
      :elevation="8"
      class="bottom-nav"
      grow
    >
      <v-btn
        v-for="item in bottomNavItems"
        :key="item.to"
        :value="item.to"
        :to="item.to"
        :ripple="true"
        class="bottom-nav__btn"
      >
        <v-icon :icon="item.icon" />
        <span>{{ item.title }}</span>
      </v-btn>
    </v-bottom-navigation>
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useTheme, useDisplay } from 'vuetify'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'

const appStore = useAppStore()
const authStore = useAuthStore()
const theme = useTheme()
const { mobile } = useDisplay()
const route = useRoute()
// Scroll detection for app bar shadow
const scrolled = ref(false)
onMounted(() => {
  window.addEventListener('scroll', () => {
    scrolled.value = window.scrollY > 8
  }, { passive: true })
})

// Initialize theme on app mount. The router navigation guard handles auth
// initialization for protected routes.
onMounted(() => {
  appStore.initThemeListener()
  theme.global.name.value = appStore.resolvedTheme
})

// Keep Vuetify theme in sync
watch(
  () => appStore.resolvedTheme,
  (newTheme) => {
    theme.global.name.value = newTheme
  }
)

const isMobile = computed(() => mobile.value)

// Primary routes that have bottom nav items (no back button needed)
const primaryRoutes = ['/', '/feeds', '/items', '/search', '/settings']
const isPrimaryRoute = computed(() => primaryRoutes.includes(route.path))

// Bottom nav active value tracks current route
const bottomNavValue = computed(() => route.path)

const bottomNavItems = [
  { title: 'Home',    icon: 'mdi-view-dashboard-outline', to: '/' },
  { title: 'Items',   icon: 'mdi-text-box-multiple-outline', to: '/items' },
  { title: 'Feeds',   icon: 'mdi-rss',                    to: '/feeds' },
  { title: 'Search',  icon: 'mdi-magnify',                to: '/search' },
  { title: 'More',    icon: 'mdi-dots-horizontal',        to: '/__more__' },
]

const allNavigationItems = [
  { title: 'Dashboard', icon: 'mdi-view-dashboard-outline', to: '/' },
  { title: 'Feeds',     icon: 'mdi-rss',                    to: '/feeds' },
  { title: 'Lists',     icon: 'mdi-playlist-star',          to: '/lists' },
  { title: 'Tags',      icon: 'mdi-tag-multiple-outline',   to: '/tags' },
  { title: 'Items',     icon: 'mdi-text-box-multiple-outline', to: '/items' },
  { title: 'Podcasts',  icon: 'mdi-podcast',                to: '/podcasts' },
  { title: 'Workflows', icon: 'mdi-graph',                  to: '/workflows' },
  { title: 'Activity',  icon: 'mdi-history',                to: '/activity' },
  { title: 'Jobs',      icon: 'mdi-cog-sync-outline',       to: '/jobs' },
  { title: 'Search',    icon: 'mdi-magnify',                to: '/search' },
  { title: 'Admin',     icon: 'mdi-shield-crown-outline',   to: '/admin', adminOnly: true },
  { title: 'Settings',  icon: 'mdi-cog-outline',            to: '/settings' },
]

const navigationItems = computed(() =>
  allNavigationItems.filter(item =>
    !item.adminOnly || authStore.currentUser?.role === 'admin'
  )
)

const drawer = computed({
  get: () => appStore.drawer,
  set: value => appStore.setDrawer(value),
})

function toggleDrawer() {
  appStore.toggleDrawer()
}

// "More" tab opens the drawer instead of navigating
watch(bottomNavValue, (val) => {
  if (val === '/__more__') {
    appStore.setDrawer(true)
  }
})

function handleCycleTheme() {
  appStore.cycleTheme()
}

async function handleLogout() {
  await authStore.logout()
}
</script>

<style scoped>
/* ─── App bar ──────────────────────────────────────────────────────────── */
.app-bar {
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  transition: box-shadow 0.2s ease;
}

/* Light mode: frosted white */
:deep(.v-theme--light) .app-bar {
  background: rgba(248, 249, 251, 0.88) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
}

/* Dark mode: frosted deep navy */
:deep(.v-theme--dark) .app-bar {
  background: rgba(10, 14, 26, 0.88) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
}

.app-bar__title .title-text {
  font-family: 'Instrument Serif', serif;
  font-size: 1.25rem;
  letter-spacing: -0.01em;
}

/* ─── Navigation drawer ────────────────────────────────────────────────── */
.nav-drawer__header {
  padding-top: calc(8px + env(safe-area-inset-top, 0px)) !important;
}

/* ─── Bottom navigation ────────────────────────────────────────────────── */
.bottom-nav {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)) !important;
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
}

:deep(.v-theme--light) .bottom-nav {
  background: rgba(248, 249, 251, 0.92) !important;
}

:deep(.v-theme--dark) .bottom-nav {
  background: rgba(10, 14, 26, 0.92) !important;
}

.bottom-nav__btn {
  font-family: 'DM Sans', sans-serif !important;
  font-size: 10px !important;
  font-weight: 500;
  letter-spacing: 0 !important;
}

/* ─── Main content offset for bottom nav ──────────────────────────────── */
.has-bottom-nav :deep(.v-container) {
  /* Extra scroll clearance below bottom nav + home indicator */
  padding-bottom: calc(56px + env(safe-area-inset-bottom, 0px) + 16px) !important;
}

/* ─── Fade transition ──────────────────────────────────────────────────── */
.fade-enter-active {
  transition: opacity 0.18s ease, transform 0.18s cubic-bezier(0.0, 0, 0.2, 1);
}
.fade-leave-active {
  transition: opacity 0.12s ease;
}
.fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.fade-leave-to {
  opacity: 0;
}
</style>
